import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  CalendarX,
  Package,
  Plus,
  RotateCw,
  Search,
  SlidersHorizontal,
  Wallet,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import stockService, { EXPIRING_DAYS } from "@/services/stockService";
import productService from "@/services/productService";
import { showError, showInfo } from "@/store/errorStore";
import StockFormModal from "./StockFormModal";
import StockMovementDialog from "./StockMovementDialog";
import StockLotCard, { getLotInfo } from "./StockLotCard";

// Lotes exibidos antes do "Ver todos"
const PAGE_SIZE = 12;

// Filtros rápidos (chips)
const FILTERS = {
  all: { label: "Todos", match: () => true },
  low: { label: "Estoque Baixo", match: (i) => i.level !== "ok" },
  expiring: {
    label: "A Vencer",
    match: (i) => i.expiration === "critical" || i.expiration === "soon",
  },
  available: { label: "Disponíveis", match: (i) => i.level === "ok" },
  reserved: {
    label: "Reservados",
    optional: true,
    match: (i) => i.reserved > 0 || i.reservations.length > 0,
  },
  expired: {
    label: "Vencidos",
    tone: "danger",
    optional: true,
    match: (i) => i.expiration === "expired",
  },
};

const byExpiration = (a, b) =>
  (a.info.days ?? Infinity) - (b.info.days ?? Infinity);

const SORTS = {
  expiration: { label: "Validade mais próxima", compare: byExpiration },
  recent: {
    label: "Mais recentes",
    compare: (a, b) => Number(b.stock.id) - Number(a.stock.id),
  },
  name: {
    label: "Produto (A–Z)",
    compare: (a, b) =>
      String(a.stock.product_name || "").localeCompare(
        String(b.stock.product_name || ""),
        "pt-BR"
      ),
  },
  available: {
    label: "Menor disponível",
    compare: (a, b) => a.info.available - b.info.available,
  },
  value: {
    label: "Maior valor",
    compare: (a, b) => b.value - a.value,
  },
};

/** Minúsculas e sem acento, para a busca */
const normalize = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function FilterChip({ active, tone = "default", onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex-shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-semibold shadow-sm transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : tone === "danger"
          ? "bg-destructive/10 text-destructive hover:bg-destructive/15"
          : "bg-card text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
    </button>
  );
}

function MetricCard({ label, icon: Icon, iconClass, onClick, active, children }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      aria-pressed={onClick ? active : undefined}
      className={cn(
        "flex flex-col justify-between rounded-xl border bg-card p-3.5 text-left shadow-soft transition-all",
        onClick && "hover:shadow-ambient active:scale-[0.99]",
        active && "border-primary ring-2 ring-primary/15"
      )}
    >
      <span className="flex w-full items-center justify-between gap-2">
        <span className="text-xs font-semibold text-muted-foreground">
          {label}
        </span>
        <span
          className={cn(
            "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full",
            iconClass
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
      </span>
      <span className="mt-2 block">{children}</span>
    </Tag>
  );
}

function ListSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-3 rounded-xl border bg-card p-4">
          <div className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-4 w-16" />
          </div>
          <Skeleton className="h-14 w-full rounded-lg" />
          <div className="flex justify-between">
            <Skeleton className="h-6 w-32 rounded-full" />
            <Skeleton className="h-4 w-40" />
          </div>
          <div className="flex justify-end gap-2">
            <Skeleton className="h-9 w-28 rounded-full" />
            <Skeleton className="h-9 w-24 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Gestão de Estoque (docs/tela/code.html): métricas, busca, filtros rápidos
 * e cards por lote com Movimentar (entrada/saída) e Ajustar (editar lote).
 */
const StocksManagement = ({ onOpenOrder }) => {
  const [stocks, setStocks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [quickFilter, setQuickFilter] = useState("all");
  const [sortBy, setSortBy] = useState("expiration");
  const [showAll, setShowAll] = useState(false);

  // Nova entrada / ajuste do lote
  const [showModal, setShowModal] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [saving, setSaving] = useState(false);

  // Movimentação (entrada/saída)
  const [movingStock, setMovingStock] = useState(null);
  const [moving, setMoving] = useState(false);

  // Exclusão e correção de reservas
  const [stockToDelete, setStockToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [adjustingCode, setAdjustingCode] = useState(null);

  useEffect(() => {
    loadData();
    // Fotos e catálogo do modal; falha aqui não impede a tela (sem modal)
    productService
      .getAdminAll({}, { silent: true })
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]));
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await stockService.getAllStocks();
      setStocks(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (error) {
      // Modal global já exibido pelo interceptor
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  // Foto do produto: o lote traz o code UUID, que é o `id` do produto
  const imageByProduct = useMemo(
    () => new Map(products.map((p) => [String(p.id), p.image_url])),
    [products]
  );

  // Situação de cada lote calculada uma vez por carga
  const entries = useMemo(
    () =>
      stocks.map((stock) => {
        const info = getLotInfo(stock);
        return {
          stock,
          info,
          value: stockService.calculateTotalValue(info.physical, stock.unit_cost),
          search: normalize(
            [
              stock.product_name,
              stock.lot_code,
              stock.storage_location_name,
              stock.id,
              // permite achar o lote pelo nº do pedido que o reservou
              ...info.reservations.map((r) => r.order_code),
            ].join(" ")
          ),
        };
      }),
    [stocks]
  );

  const stats = useMemo(() => {
    const s = {
      value: 0,
      products: new Set(),
      physical: 0,
      reserved: 0,
      low: 0,
      out: 0,
      expiring: 0,
      expired: 0,
      minDays: null,
      maxDays: null,
    };
    entries.forEach(({ stock, info, value }) => {
      s.value += value;
      s.products.add(stock.product_code || stock.product_id);
      s.physical += info.physical;
      s.reserved += info.reserved;
      if (info.level === "low") s.low += 1;
      if (info.level === "out") s.out += 1;
      if (FILTERS.expiring.match(info)) {
        s.expiring += 1;
        s.minDays = s.minDays === null ? info.days : Math.min(s.minDays, info.days);
        s.maxDays = s.maxDays === null ? info.days : Math.max(s.maxDays, info.days);
      }
      if (info.expiration === "expired") s.expired += 1;
    });
    return s;
  }, [entries]);

  const counts = useMemo(() => {
    const c = {};
    Object.entries(FILTERS).forEach(([key, f]) => {
      c[key] = entries.filter((e) => f.match(e.info)).length;
    });
    return c;
  }, [entries]);

  const visibleEntries = useMemo(() => {
    const term = normalize(searchQuery).trim();
    const match = FILTERS[quickFilter]?.match || FILTERS.all.match;
    const list = entries.filter(
      (e) => (!term || e.search.includes(term)) && match(e.info)
    );
    return [...list].sort(SORTS[sortBy]?.compare || byExpiration);
  }, [entries, searchQuery, quickFilter, sortBy]);

  // Busca/filtro novo volta para a primeira página
  useEffect(() => {
    setShowAll(false);
  }, [searchQuery, quickFilter, sortBy]);

  const shownEntries = showAll
    ? visibleEntries
    : visibleEntries.slice(0, PAGE_SIZE);
  const hasFilters = Boolean(searchQuery.trim()) || quickFilter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setQuickFilter("all");
  };

  const toggleFilter = (key) =>
    setQuickFilter((current) => (current === key ? "all" : key));

  const openCreate = () => {
    setEditingStock(null);
    setShowModal(true);
  };

  const openEdit = (stock) => {
    setEditingStock(stock);
    setShowModal(true);
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editingStock) {
        await stockService.updateStock(editingStock.id, payload);
        showInfo("Lote ajustado com sucesso", "Estoque");
      } else {
        await stockService.createStock(payload);
        showInfo("Entrada de estoque registrada com sucesso", "Estoque");
      }
      setShowModal(false);
      setEditingStock(null);
      await loadData();
    } catch (error) {
      // Erros HTTP já abrem o modal global; aqui só validação local
      if (!error.response) {
        showError(error.message || "Erro ao salvar o estoque", "Estoque");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async (delta) => {
    const stock = movingStock;
    if (!stock) return;
    setMoving(true);
    try {
      await stockService.moveStock(stock.id, delta);
      showInfo(
        `${delta > 0 ? "Entrada" : "Saída"} de ${plural(Math.abs(delta), "unidade", "unidades")} registrada em "${stock.product_name}"`,
        "Estoque"
      );
      setMovingStock(null);
      await loadData();
    } catch (error) {
      if (!error.response) {
        showError(error.message || "Erro ao movimentar o estoque", "Estoque");
      }
    } finally {
      setMoving(false);
    }
  };

  const handleAdjustReservations = async (stock) => {
    if (!stock.product_code || adjustingCode) return;
    setAdjustingCode(stock.product_code);
    try {
      const result = await stockService.adjustReservations(stock.product_code);
      showInfo(
        result?.message ||
          `Reservas do produto "${stock.product_name || ""}" ajustadas`,
        "Estoque"
      );
      await loadData();
    } catch (error) {
      // Modal global já exibido pelo interceptor
    } finally {
      setAdjustingCode(null);
    }
  };

  const handleConfirmDelete = async () => {
    const stock = stockToDelete;
    setStockToDelete(null);
    if (!stock) return;
    setDeletingId(stock.id);
    try {
      await stockService.deleteStock(stock.id);
      showInfo("Lote removido do estoque", "Estoque");
      await loadData();
    } catch (error) {
      // Modal global já exibido pelo interceptor
    } finally {
      setDeletingId(null);
    }
  };

  const total = stocks.length;
  const expiringHint =
    stats.expiring === 0
      ? `Nada nos próximos ${EXPIRING_DAYS} dias`
      : stats.maxDays === 0
      ? "Vence hoje"
      : stats.minDays === stats.maxDays
      ? plural(stats.minDays, "dia restante", "dias restantes")
      : `${stats.minDays} a ${stats.maxDays} dias restantes`;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      {/* Título e ação principal */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
            <Package className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="truncate font-display text-lg font-bold tracking-tight text-foreground md:text-2xl">
              Gestão de Estoque
            </h1>
            <p className="truncate text-xs text-muted-foreground md:text-sm">
              Controle de produtos e validades
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex h-10 flex-shrink-0 items-center gap-1.5 rounded-full bg-primary px-3.5 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95"
        >
          <Plus className="h-4 w-4" />
          Nova Entrada
        </button>
      </div>

      {/* Métricas (2x2 no celular, 4 colunas no desktop) */}
      {loading && total === 0 ? (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[104px] rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          <MetricCard
            label="Valor em Estoque"
            icon={Wallet}
            iconClass="bg-surface text-primary"
          >
            <span className="block truncate font-display text-lg font-bold tracking-tight text-primary md:text-xl">
              {formatCurrency(stats.value)}
            </span>
            <span className="block text-xs text-muted-foreground">
              {plural(stats.products.size, "produto", "produtos")} ·{" "}
              {plural(total, "lote", "lotes")}
            </span>
          </MetricCard>
          <MetricCard
            label="Quantidade Total"
            icon={Package}
            iconClass="bg-surface text-primary"
          >
            <span className="block font-display text-lg font-bold tracking-tight text-foreground md:text-xl">
              {stats.physical} un.
            </span>
            <span className="block text-xs text-muted-foreground">
              {plural(stats.reserved, "reservada", "reservadas")}
            </span>
          </MetricCard>
          <MetricCard
            label="Estoque Baixo"
            icon={AlertTriangle}
            iconClass="bg-terra-soft text-terra"
            onClick={() => toggleFilter("low")}
            active={quickFilter === "low"}
          >
            <span className="flex items-baseline gap-1.5">
              <span className="font-display text-lg font-bold text-terra md:text-xl">
                {stats.low + stats.out}
              </span>
              <span className="text-xs text-terra">
                {stats.low + stats.out === 1 ? "lote" : "lotes"}
              </span>
            </span>
            <span className="block text-xs text-muted-foreground">
              {plural(stats.out, "zerado", "zerados")} no momento
            </span>
          </MetricCard>
          <MetricCard
            label="A Vencer"
            icon={CalendarX}
            iconClass="bg-destructive/10 text-destructive"
            onClick={() => toggleFilter("expiring")}
            active={quickFilter === "expiring"}
          >
            <span className="flex items-baseline gap-1.5">
              <span className="font-display text-lg font-bold text-destructive md:text-xl">
                {stats.expiring}
              </span>
              <span className="text-xs text-destructive">
                {stats.expiring === 1 ? "lote" : "lotes"}
              </span>
            </span>
            <span className="block text-xs text-muted-foreground">
              {expiringHint}
              {stats.expired > 0 && ` · ${stats.expired} vencido${stats.expired === 1 ? "" : "s"}`}
            </span>
          </MetricCard>
        </div>
      )}

      {/* Busca + ordenação */}
      <div className="flex items-center gap-2">
        <div className="relative flex flex-1 items-center">
          <Search className="pointer-events-none absolute left-3.5 h-4 w-4 text-muted-foreground" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por produto, lote, local ou pedido..."
            aria-label="Buscar no estoque"
            className="h-10 w-full rounded-full border border-border bg-card pl-10 pr-4 text-sm text-foreground shadow-sm transition-colors placeholder:text-muted-foreground/80 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25"
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Ordenar lotes"
              title="Ordenar"
              className="relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm transition-colors hover:bg-muted hover:text-primary"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {sortBy !== "expiration" && (
                <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary" />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Ordenar por</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={sortBy} onValueChange={setSortBy}>
              {Object.entries(SORTS).map(([key, { label }]) => (
                <DropdownMenuRadioItem key={key} value={key}>
                  {label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Chips de filtro rápido (scroll horizontal no celular) */}
      <div
        className="no-scrollbar -mx-4 flex items-center gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0"
        role="group"
        aria-label="Filtrar lotes"
      >
        {Object.entries(FILTERS)
          .filter(
            ([key, f]) => !f.optional || counts[key] > 0 || quickFilter === key
          )
          .map(([key, f]) => (
            <FilterChip
              key={key}
              tone={f.tone}
              active={quickFilter === key}
              onClick={() => setQuickFilter(key)}
            >
              {f.label} ({counts[key] || 0})
            </FilterChip>
          ))}
      </div>

      {/* Lista */}
      {loading && total === 0 ? (
        <ListSkeleton />
      ) : loadError && total === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <Package className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            Não foi possível carregar o estoque
          </p>
          <button
            type="button"
            onClick={loadData}
            className="mt-4 flex h-9 items-center gap-1 rounded-full border border-border bg-surface px-3.5 text-sm font-semibold text-primary transition-colors hover:bg-muted"
          >
            <RotateCw className="h-4 w-4" />
            Tentar novamente
          </button>
        </div>
      ) : visibleEntries.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <Package className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            {hasFilters
              ? "Nenhum lote encontrado"
              : "Nenhuma entrada de estoque cadastrada"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {hasFilters
              ? "Tente outra busca ou limpe os filtros."
              : "Registre a primeira entrada para começar a vender."}
          </p>
          <button
            type="button"
            onClick={hasFilters ? clearFilters : openCreate}
            className={cn(
              "mt-4 flex h-9 items-center gap-1 rounded-full px-3.5 text-sm font-semibold transition-colors",
              hasFilters
                ? "border border-border bg-surface text-primary hover:bg-muted"
                : "bg-primary text-primary-foreground hover:bg-brand-dark"
            )}
          >
            {hasFilters ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {hasFilters ? "Limpar filtros" : "Nova Entrada"}
          </button>
        </div>
      ) : (
        <>
          <div
            className={cn(
              "grid grid-cols-1 gap-3 transition-opacity lg:grid-cols-2",
              loading && "opacity-60"
            )}
          >
            {shownEntries.map(({ stock }) => (
              <div
                key={stock.id}
                className={cn(
                  deletingId === stock.id && "pointer-events-none opacity-50"
                )}
              >
                <StockLotCard
                  stock={stock}
                  imageUrl={imageByProduct.get(String(stock.product_code))}
                  adjusting={adjustingCode === stock.product_code}
                  onMove={setMovingStock}
                  onEdit={openEdit}
                  onAdjustReservations={handleAdjustReservations}
                  onDelete={setStockToDelete}
                  onOpenOrder={onOpenOrder}
                />
              </div>
            ))}
          </div>

          {/* Rodapé: contagem e ver todos */}
          <div className="flex items-center justify-between gap-3 px-1 py-2 text-xs text-muted-foreground">
            <span>
              Exibindo {shownEntries.length} de{" "}
              {plural(visibleEntries.length, "registro", "registros")}
              {hasFilters && ` (${total} no total)`}
            </span>
            {visibleEntries.length > shownEntries.length ? (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="flex items-center gap-1 text-[13px] font-semibold text-primary hover:underline"
              >
                Ver todos
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[13px] font-semibold text-primary hover:underline"
                >
                  Limpar filtros
                </button>
              )
            )}
          </div>
        </>
      )}

      {/* Nova entrada / ajustar lote */}
      <StockFormModal
        open={showModal}
        onOpenChange={(open) => {
          setShowModal(open);
          if (!open) setEditingStock(null);
        }}
        stock={editingStock}
        onSave={handleSave}
        loading={saving}
        products={products}
      />

      {/* Movimentar (entrada/saída) */}
      <StockMovementDialog
        open={!!movingStock}
        onOpenChange={(open) => !open && setMovingStock(null)}
        stock={movingStock}
        onSave={handleMove}
        loading={moving}
      />

      {/* Confirmação de exclusão */}
      <AlertDialog
        open={!!stockToDelete}
        onOpenChange={(open) => !open && setStockToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir lote</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover o lote "{stockToDelete?.lot_code}"
              de "{stockToDelete?.product_name}"
              {Number(stockToDelete?.current_quantity) > 0 &&
                ` (${stockToDelete.current_quantity} un. em estoque)`}
              ? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StocksManagement;
